<template>
  <div class="document-body">
    <app-state v-if="loading" state="loading" title="Загружаем платежное поручение" />
    <template v-else>
      <q-banner v-if="exportState !== 'idle'" rounded :class="exportBannerClass" role="status" data-testid="payment-export-state">
        <q-spinner v-if="exportState === 'pending'" size="sm" />
        <q-icon v-else :name="exportState === 'success' ? 'check_circle' : 'error_outline'" aria-hidden="true" />
        {{ exportMessage }}
      </q-banner>

      <page-section title="Платеж" description="Сумма, назначение и банковские параметры документа.">
        <div class="document-body__amounts">
          <div><span>Сумма платежа</span><amount-cell :value="body.summ" /></div>
          <div><span>НДС</span><amount-cell :value="body.nds_summ" /></div>
          <div><span>Счет списания</span><strong>{{ body.account || body.plat_bill_num || '—' }}</strong></div>
        </div>
        <dl class="document-body__details">
          <div class="document-body__wide"><dt>Назначение платежа</dt><dd>{{ body.nazn_pl_full || '—' }}</dd></div>
          <div><dt>Вид операции</dt><dd>{{ body.vid_op || '—' }}</dd></div>
          <div><dt>Очередность платежа</dt><dd>{{ body.naz_pl_ext || '—' }}</dd></div>
          <div><dt>Вид платежа</dt><dd>{{ body.plat_kind || '—' }}</dd></div>
          <div><dt>Срок платежа</dt><dd>{{ body.ts_plat || '—' }}</dd></div>
          <div><dt>Поступило в банк</dt><dd>{{ body.postup_bank_plat || '—' }}</dd></div>
          <div><dt>Списано со счета</dt><dd>{{ body.spis_sch_plat || '—' }}</dd></div>
        </dl>
      </page-section>

      <div class="document-body__parties">
        <page-section title="Плательщик">
          <dl class="document-body__details document-body__details--single">
            <div><dt>Организация</dt><dd>{{ body.plat || '—' }}</dd></div>
            <div><dt>ИНН / КПП</dt><dd>{{ joinValues(body.seller_inn, body.plat_kpp) }}</dd></div>
            <div><dt>Банк</dt><dd>{{ body.plat_bank || '—' }}</dd></div>
            <div><dt>БИК</dt><dd>{{ body.plat_bik || '—' }}</dd></div>
            <div><dt>Расчетный счет</dt><dd>{{ body.plat_bill_num || '—' }}</dd></div>
            <div><dt>Корреспондентский счет</dt><dd>{{ body.plat_bill_num_ext || '—' }}</dd></div>
          </dl>
        </page-section>
        <page-section title="Получатель">
          <dl class="document-body__details document-body__details--single">
            <div><dt>Организация</dt><dd>{{ body.rec || '—' }}</dd></div>
            <div><dt>ИНН / КПП</dt><dd>{{ joinValues(body.cust_inn, body.rec_kpp) }}</dd></div>
            <div><dt>Банк</dt><dd>{{ body.rec_bank || '—' }}</dd></div>
            <div><dt>БИК</dt><dd>{{ body.rec_bik || '—' }}</dd></div>
            <div><dt>Расчетный счет</dt><dd>{{ body.rec_bill_num || '—' }}</dd></div>
            <div><dt>Корреспондентский счет</dt><dd>{{ body.rec_bill_num_ext || '—' }}</dd></div>
          </dl>
        </page-section>
      </div>
    </template>
  </div>
</template>

<script>
import AmountCell from '../../../../components/ui/AmountCell.vue'
import AppState from '../../../../components/ui/AppState.vue'
import PageSection from '../../../../components/ui/PageSection.vue'
import { downloadPaymentExport, paymentExportErrorMessage, paymentExportFilename } from '../../../../services/paymentExport'

export default {
  name: 'document_info_payment',
  components: { AmountCell, AppState, PageSection },
  props: { embeddedWorkbench: { type: Boolean, default: false } },
  data() {
    return {
      body: {},
      loading: true,
      infoRequestId: null,
      exportRequestId: null,
      exportState: 'idle',
      exportMessage: '',
      handlers: []
    }
  },
  computed: {
    isDownloading() { return this.exportState === 'pending' },
    exportBannerClass() {
      return this.exportState === 'error' ? 'document-body__banner document-body__banner--error' : 'document-body__banner'
    }
  },
  mounted() {
    this.listen('didReceiveDocumentInfo', this.onDocumentInfo)
    this.listen('onCreateTxtFromPayement', this.onExportResult)
    this.listen('connectionReady', this.loadDocument)
    if (this.connection.isAuthenticated) this.loadDocument()
  },
  beforeUnmount() { this.handlers.forEach(([event, handler]) => this.emitter.off(event, handler)) },
  methods: {
    listen(event, handler) { this.emitter.on(event, handler); this.handlers.push([event, handler]) },
    loadDocument() {
      this.loading = true
      this.infoRequestId = this.connection.makeRequestId('payment-info')
      this.connection.getDocumentInfo(this.infoRequestId, this.$route.params.id, this.$route.params.history_id || 'last')
    },
    onDocumentInfo(response) {
      if (response.request_id !== this.infoRequestId || response.data.document_type !== 'payment') return
      this.body = response.data.body || {}
      this.loading = false
    },
    joinValues(first, second) { return [first, second].filter(Boolean).join(' / ') || '—' },
    onDownload() {
      if (this.isDownloading) return
      this.exportState = 'pending'
      this.exportMessage = 'Готовим файл обмена 1С Клиент-Банк…'
      this.exportRequestId = this.connection.makeRequestId('payment-export')
      this.connection.createTxtFromPayement(this.exportRequestId, [this.$route.params.id])
    },
    onExportResult(response) {
      if (!this.exportRequestId || response.request_id !== this.exportRequestId) return
      this.exportRequestId = null
      if (response.type === 'error' || response.error) {
        this.exportState = 'error'
        this.exportMessage = paymentExportErrorMessage(response)
        return
      }
      const filename = paymentExportFilename(response)
      downloadPaymentExport(response)
        .then(() => { this.exportState = 'success'; this.exportMessage = `Файл ${filename} скачан.` })
        .catch(() => { this.exportState = 'error'; this.exportMessage = 'Не удалось скачать файл клиент-банка.' })
    },
    onDeleteDocument() { this.connection.deleteDocument(this.connection.makeRequestId('payment-delete'), this.$route.params.id) },
    onRestoreDocument() { this.connection.restoreDocument(this.connection.makeRequestId('payment-restore'), this.$route.params.id) }
  }
}
</script>

<style scoped>
.document-body { display: grid; gap: var(--sid-space-6); min-width: 0; }
.document-body__banner { display: flex; gap: var(--sid-space-2); background: var(--sid-color-info-surface); color: var(--sid-color-text-primary); }
.document-body__banner--error { background: var(--sid-color-danger-surface); }
.document-body__amounts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--sid-space-4); margin-bottom: var(--sid-space-5); }
.document-body__amounts div { display: grid; gap: var(--sid-space-1); min-width: 0; }
.document-body__amounts span, .document-body__details dt { color: var(--sid-color-text-secondary); font-size: var(--sid-font-size-label); }
.document-body__details { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--sid-space-4); margin: 0; }
.document-body__details--single { grid-template-columns: 1fr 1fr; }
.document-body__details div { min-width: 0; }
.document-body__details dd { margin: var(--sid-space-1) 0 0; overflow-wrap: anywhere; }
.document-body__wide { grid-column: 1 / -1; }
.document-body__parties { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--sid-space-6); }
@media (max-width: 899px) { .document-body__parties { grid-template-columns: 1fr; } }
@media (max-width: 599px) { .document-body__amounts, .document-body__details, .document-body__details--single { grid-template-columns: 1fr; } .document-body__wide { grid-column: auto; } }
</style>
